// Custom hooks for data fetching and state management
// These hooks wrap useEffect and integrate with Zustand stores

import { useState, useEffect } from 'react';
import { Product, Order, Stock } from '../types';
import { apiService } from '../services/api';
export { useIsMobile } from './useIsMobile';
export { useDiscount } from './useDiscount';
export { useMinimumOrder } from './useMinimumOrder';
export { useTabVisibility } from './useTabVisibility';

interface UseAsyncState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

// Generic hook for async operations
function useAsync<T>(
    asyncFunction: () => Promise<T>,
    dependencies: any[] = []
): UseAsyncState<T> {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await asyncFunction();
            setData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, dependencies);

    return {
        data,
        loading,
        error,
        refetch: fetchData
    };
}

// Hook for fetching products with category filtering
export function useProducts(category?: string | 'all', availableOnly: boolean = false) {
    return useAsync(
        async () => {
            const products = await apiService.getProducts(availableOnly);
            // If 'all' or undefined, return all products
            if (!category || category === 'all') {
                return products;
            }
            // Otherwise filter by specific category
            return products.filter(product => product.category === category);
        },
        [category, availableOnly]
    );
}

// Hook for fetching a single product
export function useProduct(id: string) {
    return useAsync(
        () => apiService.getProduct(id),
        [id]
    );
}

// Hook for managing product CRUD operations
export function useProductManagement() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const createProduct = async (product: Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>) => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            return await apiService.createProduct(product);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to create product';
            setSubmitError(errorMessage);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateProduct = async (id: string, product: Partial<Product>) => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            return await apiService.updateProduct(id, product);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update product';
            setSubmitError(errorMessage);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const deleteProduct = async (id: string) => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            await apiService.deleteProduct(id);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete product';
            setSubmitError(errorMessage);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const duplicateProduct = async (id: string) => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            return await apiService.duplicateProduct(id);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate product';
            setSubmitError(errorMessage);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        createProduct,
        updateProduct,
        deleteProduct,
        duplicateProduct,
        isSubmitting,
        submitError
    };
}

export function useStocks() {
    return useAsync<Stock[]>(() => apiService.getStocks(), []);
}

// Hook for order management
export function useOrderManagement() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const submitOrder = async (order: Omit<Order, 'id'>) => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            return await apiService.createOrder(order);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to submit order';
            setSubmitError(errorMessage);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        submitOrder,
        isSubmitting,
        submitError
    };
}

// Hook for fetching orders (for admin/management)
export function useOrders() {
    return useAsync(() => apiService.getOrders());
}

// Hook for health check
export function useHealthCheck() {
    return useAsync(() => apiService.healthCheck());
}

export function useEnvironment() {
    return useAsync(() => apiService.getEnvironment(), []);
}
